import { useState } from "react";
import { AlertTriangle, ArrowRight, ChevronDown, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { anchorForIssue, focusPreviewAnchor } from "./fieldAnchors";
import { SlotWaterfall } from "./SlotWaterfall";
import type { ProjectionDiff, SlotProjection } from "./slotProjection";

interface Props {
  projection: SlotProjection;
  diff: ProjectionDiff;
  hasSavedBaseline: boolean;
}

/**
 * The answer to "if I hit save, do I get slots?", in one line.
 *
 * Collapsed it costs ~64px, so the preview column fits a viewport and the
 * sticky positioning above it has something to hold. It expands only when
 * there is something to explain — the blocking issues, the before/after, and
 * the waterfall showing which rule ate the difference.
 */
export function SaveOutcomePanel({ projection, diff, hasSavedBaseline }: Props) {
  const errors = projection.issues.filter((i) => i.severity === "error");
  const warnings = projection.issues.filter((i) => i.severity === "warning");
  const notes = projection.issues.filter((i) => i.severity === "info");
  const willCreate = projection.generatedSlots > 0;
  const hasDetail = errors.length + warnings.length + notes.length > 0 || willCreate;
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        // A 3px status edge instead of a full coloured border: it reads at a
        // glance without drawing a box around the whole band.
        "border-b border-bridge-border bg-white border-l-[3px]",
        projection.windowNotApplicable
          ? "border-l-bridge-accent"
          : willCreate
            ? "border-l-bridge-success"
            : "border-l-bridge-warning",
      )}
    >
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3.5 text-left",
          hasDetail ? "cursor-pointer" : "cursor-default",
        )}
      >
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            projection.windowNotApplicable
              ? "bg-bridge-accent"
              : willCreate
                ? "bg-bridge-success shadow-[0_0_0_3px_rgba(46,125,50,.18)]"
                : "bg-bridge-subtle",
          )}
        />
        <span className="min-w-0 flex-1 text-[13.5px] leading-snug text-bridge-fg">
          {renderHeadline(projection, willCreate)}
        </span>
        {hasDetail && (
          <ChevronDown
            size={14}
            className={cn("shrink-0 text-bridge-muted transition-transform", open && "rotate-180")}
          />
        )}
      </button>

      {open && (
        <div className="border-t border-bridge-border px-3 pb-3 pt-2.5">
          {/* Before → after, so the edit's consequence is visible rather than
              only its end state. Meaningless on a first save. */}
          {hasSavedBaseline && diff.slotsDelta !== 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-bridge-bg px-3 py-2 text-[12.5px]">
              <span className="font-medium text-bridge-muted">Saved</span>
              <span className="font-mono font-semibold tabular-nums text-bridge-fg">
                {diff.previousSlots.toLocaleString()}
              </span>
              <ArrowRight size={12} className="text-bridge-subtle" />
              <span className="font-mono font-semibold tabular-nums text-bridge-accent">
                {diff.slots.toLocaleString()}
              </span>
              <span
                className={cn(
                  "ml-auto rounded-full px-1.5 py-0.5 font-mono font-semibold tabular-nums",
                  diff.slotsDelta > 0
                    ? "bg-bridge-success/10 text-bridge-success"
                    : "bg-bridge-danger/10 text-bridge-danger",
                )}
              >
                {diff.slotsDelta > 0 ? "+" : ""}
                {diff.slotsDelta.toLocaleString()}
              </span>
            </div>
          )}

          {(errors.length > 0 || warnings.length > 0) && (
            <ul className="mb-3 space-y-1">
              {[...errors, ...warnings, ...notes].map((issue) => {
                const isError = issue.severity === "error";
                return (
                  <li key={issue.id}>
                    <button
                      type="button"
                      onClick={() => focusPreviewAnchor(anchorForIssue(issue.id))}
                      className={cn(
                        "flex w-full items-start gap-1.5 rounded-md px-2 py-1.5 text-left text-[12.5px] transition-colors",
                        isError
                          ? "text-bridge-danger hover:bg-bridge-danger/5"
                          : issue.severity === "info"
                            ? "text-bridge-muted hover:bg-bridge-bg"
                            : "text-amber-700 hover:bg-amber-50",
                      )}
                    >
                      {isError ? (
                        <XCircle size={12} className="mt-px shrink-0" />
                      ) : issue.severity === "info" ? (
                        <Info size={12} className="mt-px shrink-0" />
                      ) : (
                        <AlertTriangle size={12} className="mt-px shrink-0" />
                      )}
                      <span className="flex-1">{issue.message}</span>
                      <span className="shrink-0 font-medium underline decoration-dotted opacity-70">
                        Fix
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="rounded-lg border border-bridge-border">
            <SlotWaterfall projection={projection} />
          </div>

          {/* Without this the projection reads as a promise about every property
              in scope, which it is not. */}
          <p className="mt-2.5 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-bridge-subtle">
            <Info size={11} className="mt-px shrink-0" />
            <span>
              Projected for a property with no availability set up yet. Properties that already have
              an availability entry keep it and inherit only the slot-slicing rules.
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The headline. An inactive scope generates nothing — saying otherwise while a
 * warning underneath contradicts it is how the old panel misled operators, so
 * the projected figure is demoted to a secondary clause.
 */
function renderHeadline(projection: SlotProjection, willCreate: boolean) {
  // The scope has its own availability and no default window, so there is
  // nothing here to project from — saying "no slots" would read as a fault
  // when the real answer is that the slots come from somewhere this preview
  // cannot see.
  if (projection.windowNotApplicable) {
    return (
      <>
        <strong className="font-semibold">Saving updates the slot rules</strong> — slots come from
        the property&rsquo;s own availability, so they can&rsquo;t be previewed here.
      </>
    );
  }

  if (!projection.active) {
    return (
      <>
        <strong className="font-semibold">Saving generates no slots</strong> — these settings are
        inactive.
        {projection.horizonSlots > 0 && (
          <>
            {" "}
            Would generate{" "}
            <span className="font-mono font-semibold tabular-nums text-bridge-accent">
              {projection.horizonSlots.toLocaleString()}
            </span>{" "}
            once active.
          </>
        )}
      </>
    );
  }

  if (!willCreate) {
    const first = projection.issues.find((i) => i.severity === "error");
    return (
      <>
        <strong className="font-semibold">Saving generates no slots</strong>
        {first ? ` — ${first.message.replace(/\.$/, "")}.` : "."}
      </>
    );
  }

  return (
    <>
      Generates{" "}
      <span className="font-mono font-semibold tabular-nums text-bridge-accent">
        {projection.generatedSlots.toLocaleString()}
      </span>{" "}
      slots over {projection.horizonDays} days ·{" "}
      <span className="font-mono tabular-nums">
        {projection.generatedCapacity.toLocaleString()}
      </span>{" "}
      leads
    </>
  );
}
